import React, { useState, useEffect, KeyboardEvent } from 'react';
import { X, Save, ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import { Record, ColumnInfo } from '../../types';
import { useAuth } from '../../context/AuthContext';

interface EditRecordModalProps {
  isOpen: boolean;
  onClose: () => void;
  record: Record | null;
  columns: ColumnInfo[];
  tableName: string;
  primaryKeyColumn: string;
  onSuccess: () => void;
}

const EditRecordModal: React.FC<EditRecordModalProps> = ({
  isOpen,
  onClose,
  record,
  columns,
  tableName,
  primaryKeyColumn,
  onSuccess,
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<Record>({});
  const [isSaving, setIsSaving] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isNewRecord = !record;

  useEffect(() => {
    if (record) {
      const tags = Array.isArray(record.tags) ? record.tags : [];
      setFormData({ ...record, tags });
    } else {
      const initialData: Record = {};
      columns.forEach((column) => {
        if (column.data_type === 'uuid' && column.is_primary_key) {
          return;
        }
        
        if (column.name === 'tags') {
          initialData[column.name] = [];
        } else if (column.name === 'is_published') {
          initialData[column.name] = false;
        } else if (column.default_value && !column.default_value.includes('gen_random_uuid')) {
          try {
            const defaultValue = JSON.parse(column.default_value.replace(/'/g, '"'));
            initialData[column.name] = defaultValue;
          } catch {
            initialData[column.name] = column.default_value;
          }
        } else {
          initialData[column.name] = null;
        }
      });
      setFormData(initialData);
    }
  }, [record, columns]);

  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        if (showDeleteConfirm) {
          setShowDeleteConfirm(false);
        } else {
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleEscKey as any);
    return () => {
      document.removeEventListener('keydown', handleEscKey as any);
    };
  }, [isOpen, onClose, showDeleteConfirm]);

  const handleChange = (column: ColumnInfo, value: any) => {
    if (value === '' && column.is_nullable) {
      value = null;
    }
    
    if (value !== null) {
      if (column.data_type.includes('int') && !isNaN(Number(value))) {
        value = parseInt(value, 10);
      } else if (column.data_type.includes('float') || column.data_type.includes('double') || column.data_type.includes('decimal')) {
        value = parseFloat(value);
      } else if (column.data_type === 'boolean') {
        value = value === 'true' || value === true;
      } else if (column.data_type.includes('json')) {
        try {
          if (typeof value === 'string') {
            value = JSON.parse(value);
          }
        } catch (e) {
          // Keep as string if parsing fails
        }
      } else if (column.data_type.includes('timestamp')) {
        if (value) {
          const date = new Date(value);
          value = date.toISOString();
        }
      }
    }
    
    if (column.name === 'is_published') {
      const isPublished = value === true;
      setFormData(prev => ({
        ...prev,
        is_published: isPublished,
        published_at: isPublished ? new Date().toISOString() : null
      }));
    } else {
      setFormData(prev => ({ ...prev, [column.name]: value }));
    }
  };

  const handleTagKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    }
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags?.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: Array.isArray(prev.tags) ? [...prev.tags, tag] : [tag]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: Array.isArray(prev.tags) ? prev.tags.filter(tag => tag !== tagToRemove) : []
    }));
  };

  const handleDelete = async () => {
    if (!record || !primaryKeyColumn) return;
    
    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from(tableName)
        .delete()
        .eq(primaryKeyColumn, record[primaryKeyColumn]);

      if (error) throw error;

      toast.success('Content deleted successfully');
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(`Error deleting content: ${error.message}`);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsSaving(true);
    
    try {
      const dataToSubmit = {
        ...formData,
        tags: Array.isArray(formData.tags) ? formData.tags : []
      };

      if (isNewRecord) {
        columns.forEach((column) => {
          if (column.data_type === 'uuid' && column.is_primary_key) {
            delete dataToSubmit[column.name];
          }
        });
      }
      
      let result;
      
      if (isNewRecord) {
        result = await supabase.from(tableName).insert(dataToSubmit).select();
      } else {
        result = await supabase
          .from(tableName)
          .update(dataToSubmit)
          .eq(primaryKeyColumn, record![primaryKeyColumn])
          .select();
      }
      
      if (result.error) throw result.error;
      
      toast.success(isNewRecord ? 'Content created successfully' : 'Content updated successfully');
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(`Error: ${error.message}`);
      console.error('Error saving record:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div 
      className={`fixed inset-y-0 right-0 w-full md:w-1/2 bg-white dark:bg-gray-800 shadow-xl transform transition-transform duration-300 ease-in-out ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      } z-50`}
    >
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <button
              onClick={onClose}
              className="mr-4 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-semibold">
              {isNewRecord ? 'Create New Content' : 'Edit Content'}
            </h2>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {columns
              .filter((column) => ['name', 'content'].includes(column.name))
              .map((column) => (
                <div key={column.name} className="space-y-2">
                  <label
                    htmlFor={column.name}
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    {column.name.charAt(0).toUpperCase() + column.name.slice(1).replace(/_/g, ' ')}
                    {!column.is_nullable && !column.is_primary_key && (
                      <span className="ml-1 text-red-600 dark:text-red-400">*</span>
                    )}
                  </label>
                  
                  {column.name === 'content' ? (
                    <textarea
                      id={column.name}
                      value={formData[column.name] === null ? '' : formData[column.name]}
                      onChange={(e) => handleChange(column, e.target.value)}
                      rows={8}
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring focus:ring-emerald-500 focus:ring-opacity-50 bg-white dark:bg-gray-700 py-2.5 px-3"
                      placeholder="Enter content..."
                    />
                  ) : (
                    <input
                      id={column.name}
                      type="text"
                      value={formData[column.name] === null ? '' : formData[column.name]}
                      onChange={(e) => handleChange(column, e.target.value)}
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring focus:ring-emerald-500 focus:ring-opacity-50 bg-white dark:bg-gray-700 py-2.5 px-3"
                      placeholder={`Enter ${column.name.replace(/_/g, ' ')}...`}
                    />
                  )}
                </div>
              ))}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring focus:ring-emerald-500 focus:ring-opacity-50 bg-white dark:bg-gray-700 py-2.5 px-3"
                placeholder="Add any notes or comments about this content block..."
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Tags
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {Array.isArray(formData.tags) && formData.tags.map((tag: string) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="ml-2 focus:outline-none"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  placeholder="Add a tag..."
                  className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm focus:border-emerald-500 focus:ring focus:ring-emerald-500 focus:ring-opacity-50 bg-white dark:bg-gray-700 py-2.5 px-3"
                />
                <button
                  type="button"
                  onClick={addTag}
                  className="px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Press Enter or click the plus button to add a tag
              </p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg space-y-4">
              <div className="flex items-center justify-between">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.is_published || false}
                    onChange={(e) => handleChange({ name: 'is_published', data_type: 'boolean' } as ColumnInfo, e.target.checked)}
                    className="w-4 h-4 text-emerald-500 border-gray-300 rounded focus:ring-emerald-500"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Published</span>
                </label>
                {formData.is_published && (
                  <input
                    type="datetime-local"
                    value={formData.published_at ? new Date(formData.published_at).toISOString().slice(0, 16) : ''}
                    onChange={(e) => handleChange({ name: 'published_at', data_type: 'timestamp with time zone' } as ColumnInfo, e.target.value)}
                    className="text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500 bg-white dark:bg-gray-700"
                  />
                )}
              </div>
              {formData.is_published && formData.published_at && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Published: {new Date(formData.published_at).toLocaleString()}
                </p>
              )}
            </div>

            {!isNewRecord && !showDeleteConfirm && (
              <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 pt-4 mt-6">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center justify-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Content
                </button>
              </div>
            )}

            {showDeleteConfirm && (
              <div className="shrink-0 border-t border-gray-200 dark:border-gray-700 pt-4 mt-6">
                <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400 mb-4">
                    Are you sure you want to delete this content? This action cannot be undone.
                  </p>
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(false)}
                      className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
                    >
                      {isDeleting ? 'Deleting...' : 'Yes, Delete'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </form>
        
        <div className="shrink-0 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSaving || showDeleteConfirm}
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg py-2.5 px-4 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                {isNewRecord ? 'Create Content' : 'Update Content'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditRecordModal;